/*
  Izvoz vseh podatkov tečajnika.

  Pravica do dostopa in prenosljivosti (GDPR, 15. in 20. člen) ni izpolnjena
  s tem, da podatke nekje vidiš — moraš jih lahko tudi odnesti. Zato dve
  obliki: berljiv .txt za človeka in .json za prenos v drug sistem.
*/

import { fmtDate, fullName, today } from './format.js';
import { DEFAULT_PROGRAM } from './program.js';
import { getSchoolDays, numberFlights } from './progress.js';
import { virLabel } from './viri.js';

/** Vse, kar o tečajniku hrani aplikacija, v eni strukturi. */
export function zberiPodatke({ profile, progress, flights, attendance, docs, exams, ucenje, program = DEFAULT_PROGRAM }) {
  const ALL_EX = program.all;
  return {
    izvozeno: new Date().toISOString(),
    vir: 'Perut Učilnica',
    osebni_podatki: {
      stevilka_tecajnika: profile?.stevilka ?? null,
      ime: profile?.ime ?? null,
      priimek: profile?.priimek ?? null,
      email: profile?.email ?? null,
      telefon: profile?.telefon ?? null,
      datum_rojstva: profile?.datum_rojstva ?? null,
      kraj_rojstva: profile?.kraj_rojstva ?? null,
      naslov: profile?.naslov ?? null,
      posta: profile?.posta ?? null,
      datum_tecaja: profile?.datum_tecaja ?? null,
      visina_cm: profile?.visina_cm ?? null,
      teza_kg: profile?.teza_kg ?? null,
      kontaktna_oseba: profile?.kontaktna_oseba ?? null,
      zdravstvene_omejitve: profile?.zdravstvene_omejitve ?? null,
      teorija_datum: profile?.teorija_datum ?? null,
      zdravniski_datum: profile?.zdravniski_datum ?? null,
      kje_je_izvedel_za_solo: virLabel(profile?.vir_prihoda) ?? null,
    },
    napredek: ALL_EX.flatMap((ex) =>
      ex.subs
        .map((sub, i) => {
          const s = progress?.[ex.id]?.subs?.[i];
          if (!s) return null;
          return {
            vaja: ex.title,
            pod_vaja: sub,
            status: s.status ?? (s.done ? 'odkljukano' : null),
            datum: s.date ?? null,
          };
        })
        .filter(Boolean),
    ),
    knjizica_letenja: numberFlights(flights).map((f) => ({
      zaporedna: f.seq,
      datum: f.flight_date,
      vzletisce: f.vzletisce ?? null,
      opomba: f.opomba ?? null,
      status: f.status,
    })),
    dnevi_solanja: getSchoolDays(progress, flights, attendance, program).map((d) => ({
      datum: d.date,
      vaj: d.exercises.length,
      letov: d.flights,
      opomba: d.note || null,
    })),
    nalozeni_dokumenti: Object.entries(docs ?? {}).map(([id, d]) => ({
      vrsta: id,
      datoteka: d.name,
      nalozeno: d.uploadedAt ?? null,
    })),
    zakljucni_test: (exams ?? []).map((a) => ({
      oddano: a.submitted_at,
      tocke: `${a.score}/${a.total}`,
      prag_odstotki: a.prag,
      opravljen: a.passed,
      podpis: a.podpis ?? null,
    })),
    interaktivno_ucenje: (ucenje ?? []).map((r) => ({
      vsebina: r.modul,
      stopnja: r.kljuc === 'kviz' ? 'preverjanje znanja' : r.kljuc,
      rezultat: r.kljuc === 'kviz' ? `${r.pravilnih}/${r.vseh}` : 'opravljeno',
      datum: r.updated_at ?? null,
    })),
  };
}

/** Ista vsebina v obliki, ki jo človek prebere brez orodij. */
export function kotBesedilo(d) {
  const o = d.osebni_podatki;
  const vrstice = [
    'PERUT UČILNICA — izvoz osebnih podatkov',
    `Izvoženo: ${fmtDate(d.izvozeno.slice(0, 10))}`,
    '',
    '── OSEBNI PODATKI ──',
    `Številka tečajnika: ${o.stevilka_tecajnika ?? '—'}`,
    `Ime in priimek: ${[o.ime, o.priimek].filter(Boolean).join(' ') || '—'}`,
    `Email: ${o.email ?? '—'}`,
    `Telefon: ${o.telefon ?? '—'}`,
    `Datum rojstva: ${o.datum_rojstva ? fmtDate(o.datum_rojstva) : '—'}`,
    `Kraj rojstva: ${o.kraj_rojstva ?? '—'}`,
    `Naslov: ${[o.naslov, o.posta].filter(Boolean).join(', ') || '—'}`,
    `Pričetek tečaja: ${o.datum_tecaja ? fmtDate(o.datum_tecaja) : '—'}`,
    `Višina / teža: ${o.visina_cm ?? '—'} cm / ${o.teza_kg ?? '—'} kg`,
    `Kontakt v sili: ${o.kontaktna_oseba ?? '—'}`,
    `Zdravstvene omejitve: ${o.zdravstvene_omejitve ?? '—'}`,
    `Teorija opravljena: ${o.teorija_datum ? fmtDate(o.teorija_datum) : '—'}`,
    `Zdravniški pregled: ${o.zdravniski_datum ? fmtDate(o.zdravniski_datum) : '—'}`,
    '',
    `── NAPREDEK (${d.napredek.length} zapisov) ──`,
    ...d.napredek.map(
      (p) => `${p.datum ? fmtDate(p.datum) : '—'}  ${p.status ?? '—'}  ${p.vaja} → ${p.pod_vaja}`,
    ),
    '',
    `── KNJIŽICA LETENJA (${d.knjizica_letenja.length} letov) ──`,
    ...d.knjizica_letenja.map(
      (f) => `${f.zaporedna}. ${fmtDate(f.datum)}  ${f.vzletisce ?? '—'}  ${f.status}`,
    ),
    '',
    `── DNEVI ŠOLANJA (${d.dnevi_solanja.length}) ──`,
    ...d.dnevi_solanja.map(
      (x) => `${fmtDate(x.datum)}  vaj: ${x.vaj}, letov: ${x.letov}${x.opomba ? `  (${x.opomba})` : ''}`,
    ),
    '',
    `── DOKUMENTI (${d.nalozeni_dokumenti.length}) ──`,
    ...d.nalozeni_dokumenti.map((x) => `${x.vrsta}: ${x.datoteka}${x.nalozeno ? `  (${x.nalozeno})` : ''}`),
    '',
    `── ZAKLJUČNI TEST (${d.zakljucni_test.length} poskusov) ──`,
    ...d.zakljucni_test.map(
      (t) =>
        `${fmtDate(t.oddano.slice(0, 10))}  ${t.tocke}  ${t.opravljen ? 'opravljen' : 'ni opravljen'}`,
    ),
    '',
    `── INTERAKTIVNO UČENJE (${d.interaktivno_ucenje.length}) ──`,
    ...d.interaktivno_ucenje.map((u) => `${u.vsebina} · ${u.stopnja}: ${u.rezultat}`),
  ];
  return vrstice.join('\n');
}

/** Sproži prenos obeh datotek. */
export function prenesiIzvoz(vsi) {
  const ime = (fullName(vsi.profile) ?? 'tecajnik')
    .replace(/[čČ]/g, 'c')
    .replace(/[šŠ]/g, 's')
    .replace(/[žŽ]/g, 'z')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .toLowerCase();

  const podatki = zberiPodatke(vsi);

  shrani(`perut-moji-podatki-${ime}-${today()}.txt`, kotBesedilo(podatki), 'text/plain');
  shrani(
    `perut-moji-podatki-${ime}-${today()}.json`,
    JSON.stringify(podatki, null, 2),
    'application/json',
  );
  return podatki;
}

function shrani(ime, vsebina, tip) {
  const blob = new Blob(['\ufeff' + vsebina], { type: `${tip};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ime;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
